/**
 * Custom hook for managing cross-chain data analysis
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { CrossPostMatch, LoadingState } from '../types/index';
import { analyzePostsBatch, fetchRecentHivePosts } from '../api/crossChainApi';
import { toDateOrNull } from '../utils/dateHelpers';

const LOOKBACK_HOURS = 8;
const AUTO_REFRESH_MS = 2 * 60 * 1000;

export interface AnalysisCacheInfo {
  generatedAt: string | null;
  source: 'none' | 'server-cache' | 'server-fresh' | 'client-direct';
  truncated?: boolean;
  analyzedPosts?: number;
  totalRecentPosts?: number;
}

function sortByCreatedDesc(rows: CrossPostMatch[]): CrossPostMatch[] {
  const sorted = [...rows];
  sorted.sort((a, b) => {
    const ad = toDateOrNull(a.hivePost.created)?.getTime() || 0;
    const bd = toDateOrNull(b.hivePost.created)?.getTime() || 0;
    return bd - ad;
  });
  return sorted;
}

function normalizeApiRows(rows: unknown): CrossPostMatch[] {
  if (!Array.isArray(rows)) return [];

  return sortByCreatedDesc(
    rows.map((row) => {
      const r = row as CrossPostMatch & {
        steemitStatus?: { last?: string | null };
        blurtStatus?: { last?: string | null };
      };

      return {
        ...r,
        steemitStatus: {
          ...(r.steemitStatus || { active: false, error: true }),
          last: toDateOrNull(r.steemitStatus?.last || undefined),
        },
        blurtStatus: {
          ...(r.blurtStatus || { active: false, error: true }),
          last: toDateOrNull(r.blurtStatus?.last || undefined),
        },
      };
    })
  );
}

export function useCrossChainAnalysis() {
  const [data, setData] = useState<CrossPostMatch[]>([]);
  const [loading, setLoading] = useState<LoadingState>({
    isLoading: false,
    progress: 0,
    currentStep: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [cacheInfo, setCacheInfo] = useState<AnalysisCacheInfo>({
    generatedAt: null,
    source: 'none',
    truncated: false,
  });
  const autoSyncEnabledRef = useRef(false);

  const analyze = useCallback(async (options?: { background?: boolean }) => {
    const background = options?.background ?? false;
    const isLocalDev =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    if (!background) {
      autoSyncEnabledRef.current = true;
    }

    if (!background) {
      setLoading({
        isLoading: true,
        progress: 0,
        currentStep: `Checking new HIVE posts (last ${LOOKBACK_HOURS}h)...`,
      });
      setError(null);
    }

    try {
      if (!background) {
        setLoading({
          isLoading: true,
          progress: 25,
          currentStep: `Fetching cached analysis from server (last ${LOOKBACK_HOURS}h)...`,
        });
      }

      let rows: CrossPostMatch[] = [];
      let fromServerCache = false;

      try {
        const response = await fetch(`/api/analysis?hours=${LOOKBACK_HOURS}`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const payload = await response.json();
        rows = normalizeApiRows(payload?.rows);
        fromServerCache = true;
        const cacheHeader = response.headers.get('X-Hive-Crosscheck-Cache');
        setCacheInfo({
          generatedAt: payload?.generatedAt || new Date().toISOString(),
          source: cacheHeader === 'memory-hit' ? 'server-cache' : 'server-fresh',
          truncated: !!payload?.truncated,
          analyzedPosts:
            typeof payload?.analyzedPosts === 'number' ? payload.analyzedPosts : undefined,
          totalRecentPosts:
            typeof payload?.totalRecentPosts === 'number' ? payload.totalRecentPosts : undefined,
        });
      } catch (apiError) {
        if (!isLocalDev) {
          throw apiError instanceof Error
            ? apiError
            : new Error('API route /api/analysis unavailable');
        }
        console.warn('[Hook] /api/analysis unavailable in local dev, falling back to direct RPC:', apiError);
      }

      if (!fromServerCache) {
        if (!background) {
          setLoading({
            isLoading: true,
            progress: 50,
            currentStep: `Fetching/analyzing last ${LOOKBACK_HOURS}h directly...`,
          });
        }

        const hivePosts = await fetchRecentHivePosts(LOOKBACK_HOURS);
        if (hivePosts.length === 0) {
          rows = [];
        } else {
          rows = sortByCreatedDesc(await analyzePostsBatch(hivePosts));
        }
        setCacheInfo({
          generatedAt: new Date().toISOString(),
          source: 'client-direct',
          truncated: false,
        });
      }

      setData(rows);

      if (!background) {
        setLoading({ isLoading: true, progress: 100, currentStep: 'Complete!' });
        setTimeout(() => {
          setLoading({ isLoading: false, progress: 100, currentStep: '' });
        }, 500);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error('[Hook] Analysis error:', message);
      if (!background) {
        setError(`Analysis failed: ${message}`);
        setLoading({ isLoading: false, progress: 0, currentStep: '' });
      }
    }
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (autoSyncEnabledRef.current) {
        void analyze({ background: true });
      }
    }, AUTO_REFRESH_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [analyze]);

  const refresh = useCallback(() => {
    setError(null);
    analyze();
  }, [analyze]);

  return { data, loading, error, analyze, refresh, cacheInfo };
}
